import { textVariants } from "@/src/components/Text/Text";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/shad/card";
import { useWalletData } from "@/src/hooks/useCoinData";
import PriceChart from "../../Home/components/PriceChart";
import { cn } from "@/lib/utils";
import { formatNumber, getUniqueArrayByKey } from "@/src/utils";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/src/components/shad/accordion";
import { MiniLoader } from "@/src/components/Loader";

type TSubcoin = {
  id: number;
  label: string;
  ticker: string;
  image: string;
  fiat_balance: string;
  crypto_balance: string;
  subcoins: WalletDataInfo[] | undefined;
  totalSubCoinsCryptoBalance: number;
  totalSubCoinsFiatBalance: number;
};

const Overview = () => {
  const { walletData, isLoading } = useWalletData();
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.wallet.overview.${key}`);

  const groupedSubCoins = walletData?.wallets?.map((coin) => {
    const subcoins = walletData?.wallets?.filter((item) => {
      return item?.ticker === coin?.ticker;
    });
    return {
      id: coin?.coin__id,
      label: coin?.label,
      ticker: coin?.ticker,
      image: coin?.image,
      fiat_balance: coin?.fiat_balance,
      crypto_balance: coin?.crypto_balance,
      subcoins: subcoins?.length > 1 ? subcoins : undefined,
      totalSubCoinsCryptoBalance:
        subcoins?.length > 1
          ? subcoins?.reduce(
              (acc, curr) => acc + Number(curr?.crypto_balance),
              0
            )
          : coin?.crypto_balance,
      totalSubCoinsFiatBalance:
        subcoins?.length > 1
          ? subcoins?.reduce((acc, curr) => acc + Number(curr?.fiat_balance), 0)
          : coin?.fiat_balance,
    };
  });

  const uniqueGroupedSubCoins =
    (groupedSubCoins &&
      getUniqueArrayByKey<TSubcoin>(groupedSubCoins as TSubcoin[], "ticker")) ??
    [];

  if (!uniqueGroupedSubCoins) return null;

  return (
    <div>
      <div className="grid lg:grid-cols-5 lg:gap-5 space-y-5 lg:space-y-0 mt-5">
        <div className="lg:col-span-3">
          <Card className="p-4 h-[55vh]">
            <PriceChart />
          </Card>
        </div>
        <div className="lg:col-span-2">
          <Card className="h-[55vh] overflow-y-scroll">
            <CardHeader className="sticky top-0 z-10 bg-white">
              <CardTitle>{trans("balances.header")}</CardTitle>
            </CardHeader>
            {isLoading ? (
              <MiniLoader />
            ) : (
              <CardContent>
                <div className="grid gap-5">
                  {uniqueGroupedSubCoins?.map((coin, index) =>
                    coin?.subcoins === undefined ? (
                      <CoinRender key={index} coin={coin} />
                    ) : (
                      <Accordion
                        key={index}
                        type="single"
                        collapsible
                        className="w-full flex flex-col -my-4"
                      >
                        <AccordionItem
                          value={coin?.label}
                          className=" rounded-md"
                        >
                          <AccordionTrigger
                          // className={cn(textVariants({ font: "semibold" }))}
                          >
                            <CoinRender coin={coin} />
                            {/* <div className="text-left">{item?.title}</div> */}
                          </AccordionTrigger>
                          <AccordionContent className="grid gap-2 rounded-b-md pb-0 py-4 px-3 dark:bg-grey-600 bg-gray-50 border border-gray-200 rounded-lg">
                            {coin?.subcoins?.map((coin, index) => (
                              <SubCoinRender key={index} coin={coin} />
                            ))}
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    )
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Overview;

const CoinRender = ({ coin }: { coin: TSubcoin }) => (
  <div className="flex items-center justify-between w-full">
    <div className="flex items-center gap-2">
      <img src={coin?.image} className="h-5 w-5" alt={coin?.ticker} />
      <div className="flex flex-col">
        <span
          className={cn(
            textVariants({
              case: "uppercase",
              font: "semibold",
              size: "sm",
            }),
            "text-left"
          )}
        >
          {coin?.ticker}
        </span>
        <span
          className={textVariants({
            size: "xs",
            variant: "desc",
          })}
        >
          {coin?.label}
        </span>
      </div>
    </div>
    <div className="grid">
      <span
        className={cn(
          textVariants({
            size: "sm",
            font: "semibold",
          }),
          "text-right"
        )}
      >
        $ {formatNumber(coin?.totalSubCoinsFiatBalance)}
      </span>
      <span className={cn(textVariants({ size: "xs" }), "text-right")}>
        {coin?.totalSubCoinsCryptoBalance}
      </span>
    </div>
  </div>
);

const SubCoinRender = ({ coin }: { coin: WalletDataInfo }) => (
  <div className="flex items-center justify-between w-full">
    <div className="flex items-center gap-2">
      <img src={coin?.network_image} className="h-5 w-5" alt={coin?.ticker} />
      <div className="flex flex-col">
        <span
          className={textVariants({
            case: "uppercase",
            font: "semibold",
            size: "sm",
          })}
        >
          {coin?.network_label}
        </span>
        <span
          className={textVariants({
            size: "xs",
            variant: "desc",
            case: "uppercase",
          })}
        >
          {coin?.ticker}
        </span>
      </div>
    </div>
    <div className="grid">
      <span
        className={cn(
          textVariants({
            size: "sm",
            font: "semibold",
          }),
          "text-right"
        )}
      >
        $ {formatNumber(coin?.fiat_balance)}
      </span>
      <span className={cn(textVariants({ size: "xs" }), "text-right")}>
        {coin?.crypto_balance}
      </span>
    </div>
  </div>
);
